import React, {useState} from "react";
import moment from "moment";
import {IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Stack, Tooltip} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {
  ArrowLeft01Icon, ArrowRight01Icon, Calendar03Icon, Clock01Icon, Tick02Icon, Timer02Icon
} from "@hugeicons/core-free-icons";
import {STATISTIC_PERIODS} from "../../../../repositories/DashboardRepository";
import {findPeriod, resolveResponseTimeWindow} from "../useProjectStatistics";
import {LiveDot, MetricCard, NoChartData} from "./MetricCard";
import {Sparkline} from "./Sparkline";
import {csvFileName, exportCsv, ExportButton} from "./ExportButton";

export const TIME_FORMATS = {MINUTES: "HH:mm", HOURLY: "DD MMM HH:mm", DAYS: "DD MMM"};

const CHART_HEIGHT = 96;

const formatSeconds = (value) => `${(Number(value) || 0).toFixed(2)}s`;

const navButtonSx = {
  pointerEvents: "auto",
  width: 24,
  height: 24,
  borderRadius: "50% !important",
  bgcolor: "#fff",
  color: "#4A4A63",
  boxShadow: "0 1px 5px rgba(35, 38, 77, 0.18)",
  "&:hover": {bgcolor: "#F4F4FA"},
  "&.Mui-disabled": {bgcolor: "#fff", opacity: 0.4},
};

const PERIOD_ICONS = {MINUTES: Timer02Icon, HOURLY: Clock01Icon, DAYS: Calendar03Icon};

// Bucket size of one card (minute / hour / day); picking one jumps back to the live window
export const PeriodMenu = ({period, onChange}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const current = findPeriod(period);

  return (
    <>
      <Tooltip title={`By ${current.label.toLowerCase()} · ${current.windowLabel}`}>
        <IconButton
          size={"small"}
          onClick={(e) => setAnchorEl(e.currentTarget)}
          aria-label={"Select time period"}
          sx={{borderRadius: "50% !important", color: "#8A8AA0", "&:hover": {bgcolor: "#F4F4FA", color: "#4A4A63"}}}
        >
          <HugeiconsIcon icon={PERIOD_ICONS[period]} size={17}/>
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{vertical: "bottom", horizontal: "right"}}
        transformOrigin={{vertical: "top", horizontal: "right"}}
        PaperProps={{sx: {borderRadius: "12px", mt: 0.5, minWidth: 190}}}
      >
        {STATISTIC_PERIODS.map(option => (
          <MenuItem
            key={option.value}
            dense
            selected={option.value === period}
            onClick={() => {
              setAnchorEl(null);
              onChange(option.value);
            }}
            sx={{gap: 1}}
          >
            <ListItemIcon sx={{minWidth: "24px !important", color: "#6B6B80"}}>
              <HugeiconsIcon icon={PERIOD_ICONS[option.value]} size={18}/>
            </ListItemIcon>
            <ListItemText
              primary={option.label}
              secondary={option.windowLabel}
              secondaryTypographyProps={{fontSize: 11}}
            />
            {option.value === period && <HugeiconsIcon icon={Tick02Icon} size={16}/>}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

// Left / right chevrons over the chart, moving the window by half its size; right stops at now
export const WindowNavigation = ({step, isLive, onShift}) => (
  <Stack direction={"row"} justifyContent={"space-between"} alignItems={"center"}
         sx={{position: "absolute", inset: 0, top: 12, px: 1.5, pointerEvents: "none"}}>
    <Tooltip title={`Back ${step}`}>
      <IconButton size={"small"} sx={navButtonSx} onClick={() => onShift(-1)}>
        <HugeiconsIcon icon={ArrowLeft01Icon} size={14}/>
      </IconButton>
    </Tooltip>
    <Tooltip title={isLive ? "Showing latest" : `Forward ${step}`}>
      <span style={{pointerEvents: "auto"}}>
        <IconButton size={"small"} sx={navButtonSx} disabled={isLive} onClick={() => onShift(1)}>
          <HugeiconsIcon icon={ArrowRight01Icon} size={14}/>
        </IconButton>
      </span>
    </Tooltip>
  </Stack>
);

// Period, time format and label of a card's window ({period, end})
const describeWindow = (window) => {
  const {period, start, end} = resolveResponseTimeWindow(window);
  const periodConfig = findPeriod(period);
  const isLive = !window?.end;
  const timeFormat = TIME_FORMATS[period];
  const label = isLive ? periodConfig.windowLabel : `${start.format(timeFormat)} – ${end.format(timeFormat)}`;
  return {period, isLive, timeFormat, label, step: `${periodConfig.step.amount} ${periodConfig.step.unit}`};
};

// Metric card whose chart shows a statistic ({items: [{timestamp, max}]}) over a movable window, with the
// bucket-size menu and the back / forward chevrons. formatY renders a bucket's value in the tooltip.
const WindowedMetricCard = ({
                              type, label, icon, color, statistic, window, value, note, loading, formatY, decimals,
                              exportHeader, exportRow, onChangePeriod, onShift
                            }) => {
  const {period, isLive, timeFormat, step} = describeWindow(window);
  const points = (statistic?.items || []).map(item => ({x: item.timestamp, y: item.max}));
  const hasData = points.some(point => Number(point.y) > 0);

  return (
    <MetricCard
      icon={icon}
      color={color}
      label={label}
      loading={loading}
      value={value}
      note={note}
      action={
        <Stack direction={"row"} alignItems={"center"}>
          <ExportButton
            disabled={!statistic?.items?.length}
            onClick={() => exportCsv(
              [exportHeader, ...(statistic?.items || []).map(exportRow)],
              csvFileName(`${type.toLowerCase().replaceAll("_", "-")}-${period.toLowerCase()}`)
            )}
          />
          <PeriodMenu period={period} onChange={(next) => onChangePeriod(type, next)}/>
        </Stack>
      }
      chart={hasData
        ? (
          <Sparkline
            name={label}
            points={points}
            color={color}
            height={CHART_HEIGHT}
            formatX={(x) => moment(x).format(timeFormat)}
            formatY={formatY}
            decimals={decimals}
          />
        )
        : <NoChartData height={CHART_HEIGHT}/>}
      chartOverlay={
        <WindowNavigation step={step} isLive={isLive} onShift={(direction) => onShift(type, direction)}/>
      }
    />
  );
};

// Average response time of one type (CHATBOT / TEXT_TO_SPEECH / SPEECH_TO_TEXT) over the card's window,
// with the max per bucket as a sparkline
export const ResponseTimeCard = ({statistic, loading, window, ...props}) => (
  <WindowedMetricCard
    {...props}
    statistic={statistic}
    window={window}
    loading={loading && !statistic}
    value={statistic ? formatSeconds(statistic.avg) : "–"}
    note={
      <Tooltip title={statistic ? `Max ${formatSeconds(statistic.max)}` : ""}>
        <span>Avg. latency · {describeWindow(window).label}</span>
      </Tooltip>
    }
    formatY={(y) => `Max ${formatSeconds(y)}`}
    decimals={1}
    exportHeader={["Timestamp", "Avg (s)", "Max (s)"]}
    exportRow={(item) => [item.timestamp, item.avg, item.max]}
  />
);

// Calls active right now, with the peak of concurrent calls per bucket as a sparkline
export const ActiveCallsCard = ({activeCalls, statistic, window, ...props}) => (
  <WindowedMetricCard
    {...props}
    statistic={statistic}
    window={window}
    loading={activeCalls === null}
    value={activeCalls ?? "–"}
    note={<><LiveDot/> Live right now</>}
    decimals={0}
    exportHeader={["Timestamp", "Peak active calls"]}
    exportRow={(item) => [item.timestamp, Math.round(Number(item.max) || 0)]}
    formatY={(y) => {
      const calls = Math.round(Number(y) || 0);
      return `Peak ${calls} ${calls === 1 ? "call" : "calls"}`;
    }}
  />
);
